/**
 * A Google Maps OverlayView that renders a static image pinned to geographic bounds.
 * @class
 * @name CustomImageOverlay
 * @extends {google.maps.OverlayView}
 * @param {string} url - The image URL to display
 * @param {{ north: number, south: number, east: number, west: number }} bounds
 * @param {CustomImageOverlayOptions} [options={}]
 */

/**
 * @typedef {Object} CustomImageOverlayOptions
 * @property {number} [rotation=0] - Rotation of the image in degrees
 * @property {number} [opacity=100] - Opacity of the image as a percentage
 * @property {number} [relative_anchor_x=0.5] - X coordinate of the rotation anchor point as a fraction of the image width
 * @property {number} [relative_anchor_y=0.5] - Y coordinate of the rotation anchor point as a fraction of the image height
 */

/**
 * Creates a new CustomImageOverlay instance pinned to geographic bounds.
 * Must be called after the Google Maps script has loaded.
 * @param {string} imageUrl - The image URL to display
 * @param {{ north: number, south: number, east: number, west: number }} startingImageBounds
 * @param {CustomImageOverlayOptions} [options] - Optional settings for the overlay
 * @returns {CustomImageOverlay}
 */
export function createCustomImageOverlay(imageUrl, startingImageBounds, options = {}) {
  // The class needs to be defined inside the function because google.maps is not available when the file is imported
  const CustomImageOverlay = class extends google.maps.OverlayView {
    /** @type {string} */
    #imageUrl;
    /** @type {google.maps.LatLngBounds} */
    #imageBounds;
    /** @type {CustomImageOverlayOptions} */
    #options;
    /** @type {HTMLDivElement | null} */
    #overlayDiv = null;

    /**
     * @param {string} url - The image URL to display
     * @param {{ north: number, south: number, east: number, west: number }} bounds
     * @param {CustomImageOverlayOptions} options
     */
    constructor(url, bounds, options) {
      super();
      this.#imageUrl = url;
      this.#options = options;
      this.#imageBounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(bounds.south, bounds.west),
        new google.maps.LatLng(bounds.north, bounds.east)
      );
    }

    onAdd() {
      // Create the image and set the styles for it
      const { opacity } = this.#options;
      const imageElement = document.createElement('img');
      imageElement.src = this.#imageUrl;
      Object.assign(imageElement.style, {
        position: 'absolute',
        width: '100%',
        height: '100%',
        opacity: (opacity ?? 100) / 100,
        pointerEvents: 'none'
      });

      // Create a div to hold the image
      const overlayDiv = document.createElement('div');
      overlayDiv.style.cssText = 'position:absolute;pointer-events:none;';
      overlayDiv.appendChild(imageElement);

      // Add the overlay to overlayLayer so it shows up below the map markers and connections but above the super zoom overlay
      this.getPanes().overlayLayer.appendChild(overlayDiv);
      this.#overlayDiv = overlayDiv;
    }

    /**
     * Replaces the stored bounds and options with the values from edited layer data and redraws.
     * @param {{ north: number, south: number, east: number, west: number }} bounds
     * @param {CustomImageOverlayOptions} [options]
     */
    redrawWithUpdates(bounds, options = {}) {
      if (!bounds) return;

      // Replace the stored bounds and options with the new values from the edited layer data
      this.#imageBounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(bounds.south, bounds.west),
        new google.maps.LatLng(bounds.north, bounds.east)
      );
      this.#options = options;

      // Re-apply the image opacity since draw only handles bounds and rotation
      const imageElement = this.#overlayDiv?.firstElementChild;
      if (imageElement) imageElement.style.opacity = (options.opacity ?? 100) / 100;

      // Redraw so the new bounds and rotation take effect immediately
      this.draw();
    }

    draw() {
      if (!this.#overlayDiv) return;
      const proj = this.getProjection();
      const southWest = proj.fromLatLngToDivPixel(this.#imageBounds.getSouthWest());
      const northEast = proj.fromLatLngToDivPixel(this.#imageBounds.getNorthEast());
      const imageWidth = northEast.x - southWest.x;
      const imageHeight = southWest.y - northEast.y;
      Object.assign(this.#overlayDiv.style, {
        left: `${southWest.x}px`,
        top: `${northEast.y}px`,
        width: `${imageWidth}px`,
        height: `${imageHeight}px`
      });

      // Apply the rotation around the anchor point
      const { rotation = 0, relative_anchor_x = 0.5, relative_anchor_y = 0.5 } = this.#options;
      // Multiply by the image dimensions so that the anchor point scales with the image as the user zooms in and out
      this.#overlayDiv.style.transformOrigin = `${relative_anchor_x * imageWidth}px ${relative_anchor_y * imageHeight}px`;
      this.#overlayDiv.style.transform = `rotate(${rotation}deg)`;
    }

    onRemove() {
      this.#overlayDiv?.parentNode?.removeChild(this.#overlayDiv);
      this.#overlayDiv = null;
    }
  };
  return new CustomImageOverlay(imageUrl, startingImageBounds, options);
}
